// src/pages/finance-portal/customers/CustomerForm.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, TextField, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Select, FormControl, InputLabel, Alert,
    CircularProgress, FormControlLabel, Checkbox, Divider, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, TablePagination, Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';
import { useChatbots } from '../../../contexts/ChatbotContext';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const PAYMENT_TERMS_OPTIONS = [
    { value: 'DUE_ON_RECEIPT', label: 'Due on Receipt' },
    { value: 'NET_15', label: 'Net 15' },
    { value: 'NET_30', label: 'Net 30' },
    { value: 'NET_60', label: 'Net 60' },
    { value: 'NET_90', label: 'Net 90' },
];

const emptyAddress = { street: '', city: '', state: '', zip: '', country: '' };

const initialFormData = {
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    payment_terms: 'NET_30',
    credit_limit: '',
    credit_limit_warning_pct: 80,
    currency: 'USD',
    tax_id: '',
    tax_exempt: false,
    billing_address: { ...emptyAddress },
    shipping_address: { ...emptyAddress },
    notes: '',
    tags: '',
    crm_contact_id: null,
};

export default function CustomerForm({ open, onClose, onSaved, customer }) {
    const { enqueueSnackbar } = useSnackbar();
    const { activeChatbot } = useChatbots();
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({ ...initialFormData });
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // CRM Contact Selection state
    const [step, setStep] = useState('select'); // 'select' | 'form'
    const [crmContacts, setCrmContacts] = useState([]);
    const [crmLoading, setCrmLoading] = useState(false);
    const [crmSearch, setCrmSearch] = useState('');
    const [crmPage, setCrmPage] = useState(0);
    const [crmTotal, setCrmTotal] = useState(0);
    const [selectedContact, setSelectedContact] = useState(null);

    const isEdit = !!customer;

    const fetchCrmContacts = useCallback(async () => {
        setCrmLoading(true);
        try {
            const params = { page: crmPage + 1, per_page: 10 };
            if (crmSearch) params.search = crmSearch;
            const res = await axiosInstance.get('/api/finance/crm-contacts', { params });
            if (res.data.success) {
                setCrmContacts(res.data.data || []);
                setCrmTotal(res.data.total || 0);
            }
        } catch (err) {
            // silent
        } finally {
            setCrmLoading(false);
        }
    }, [crmPage, crmSearch]);

    useEffect(() => {
        if (open && !isEdit && step === 'select') {
            fetchCrmContacts();
        }
    }, [open, isEdit, step, fetchCrmContacts]);

    useEffect(() => {
        if (open) {
            setError(null);
            if (customer) {
                setStep('form');
                setSelectedContact(customer.crm_contact_id ? {
                    id: customer.crm_contact_id,
                    full_name: customer.crm_contact_name,
                    email: customer.crm_contact_email,
                } : null);
                setFormData({
                    company_name: customer.company_name || '',
                    first_name: customer.first_name || '',
                    last_name: customer.last_name || '',
                    email: customer.email || '',
                    phone: customer.phone || '',
                    payment_terms: customer.payment_terms || 'NET_30',
                    credit_limit: customer.credit_limit != null ? String(customer.credit_limit) : '',
                    credit_limit_warning_pct: customer.credit_limit_warning_pct != null ? customer.credit_limit_warning_pct : 80,
                    currency: customer.currency || 'USD',
                    tax_id: customer.tax_id || '',
                    tax_exempt: customer.tax_exempt || false,
                    billing_address: customer.billing_address
                        ? (typeof customer.billing_address === 'string' ? JSON.parse(customer.billing_address) : { ...emptyAddress, ...customer.billing_address })
                        : { ...emptyAddress },
                    shipping_address: customer.shipping_address
                        ? (typeof customer.shipping_address === 'string' ? JSON.parse(customer.shipping_address) : { ...emptyAddress, ...customer.shipping_address })
                        : { ...emptyAddress },
                    notes: customer.notes || '',
                    tags: Array.isArray(customer.tags) ? customer.tags.join(', ') : (customer.tags || ''),
                    crm_contact_id: customer.crm_contact_id || null,
                });
            } else {
                setStep('select');
                setSelectedContact(null);
                setCrmSearch('');
                setCrmPage(0);
                setFormData({ ...initialFormData, billing_address: { ...emptyAddress }, shipping_address: { ...emptyAddress } });
            }
        }
    }, [open, customer]);

    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setFormData(prev => ({
            ...prev,
            crm_contact_id: contact.id,
            first_name: contact.first_name || '',
            last_name: contact.last_name || '',
            email: contact.email || '',
            phone: contact.phone || '',
            company_name: contact.company_name || '',
        }));
        setStep('form');
    };

    const handleSkipCrm = () => {
        setSelectedContact(null);
        setFormData({ ...initialFormData, billing_address: { ...emptyAddress }, shipping_address: { ...emptyAddress }, crm_contact_id: null });
        setStep('form');
    };

    const handleAddCrmContact = () => {
        const slug = activeChatbot?.chatbot_identity || activeChatbot?.id;
        if (!slug) {
            enqueueSnackbar('Please select a chatbot first to add a CRM contact', { variant: 'warning' });
            return;
        }
        onClose();
        navigate(`/chatbot/${slug}/crm/contacts`, {
            state: {
                fromFinancePortal: true,
                preselectContactType: 'CUSTOMER',
                returnPath: location.pathname + location.search,
                returnAction: 'addCustomer',
                reason: 'Add a CRM contact to link as an AR customer.',
            },
        });
    };

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const updateAddress = (type, field, value) => {
        setFormData(prev => ({
            ...prev,
            [type]: { ...prev[type], [field]: value },
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const tagsArray = formData.tags
                ? formData.tags.split(',').map(t => t.trim()).filter(Boolean)
                : [];

            const payload = {
                company_name: formData.company_name || null,
                first_name: formData.first_name || null,
                last_name: formData.last_name || null,
                email: formData.email || null,
                phone: formData.phone || null,
                payment_terms: formData.payment_terms,
                credit_limit: formData.credit_limit ? parseFloat(formData.credit_limit) : null,
                credit_limit_warning_pct: formData.credit_limit_warning_pct ? parseInt(formData.credit_limit_warning_pct, 10) : 80,
                currency: formData.currency,
                tax_id: formData.tax_id || null,
                tax_exempt: formData.tax_exempt,
                billing_address: formData.billing_address,
                shipping_address: formData.shipping_address,
                notes: formData.notes || null,
                tags: tagsArray,
            };

            if (!isEdit && formData.crm_contact_id) {
                payload.crm_contact_id = formData.crm_contact_id;
            }

            if (isEdit) {
                await axiosInstance.put(`/api/finance/customers/${customer.id}`, payload);
                enqueueSnackbar('Customer updated successfully', { variant: 'success' });
            } else {
                await axiosInstance.post('/api/finance/customers', payload);
                enqueueSnackbar('Customer created successfully', { variant: 'success' });
            }

            onSaved?.();
            onClose();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save customer';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar(msg, { variant: 'warning' });
            } else {
                setError(msg);
                enqueueSnackbar(msg, { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    const renderAddressFields = (type, label) => (
        <Box>
            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField
                    label="Street" size="small" fullWidth
                    value={formData[type].street}
                    onChange={e => updateAddress(type, 'street', e.target.value)}
                    InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                        label="City" size="small" fullWidth
                        value={formData[type].city}
                        onChange={e => updateAddress(type, 'city', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <TextField
                        label="State" size="small" sx={{ minWidth: 120 }}
                        value={formData[type].state}
                        onChange={e => updateAddress(type, 'state', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <TextField
                        label="ZIP Code" size="small" sx={{ minWidth: 120 }}
                        value={formData[type].zip}
                        onChange={e => updateAddress(type, 'zip', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <TextField
                        label="Country" size="small" fullWidth
                        value={formData[type].country}
                        onChange={e => updateAddress(type, 'country', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                </Box>
            </Box>
        </Box>
    );

    // ---- CRM Contact Selection Step ----
    const renderCrmSelection = () => (
        <>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonSearchIcon sx={{ color: ACCENT }} />
                    Select CRM Contact
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.8rem' }}>
                    Select a CRM contact to link as an AR customer. Their name, email, and phone will be auto-populated. Don't see your contact? Click <strong>Add to CRM</strong> below to create one in the CRM, then come back here to link it.
                </Alert>

                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search contacts by name, email, or phone..."
                    value={crmSearch}
                    onChange={e => { setCrmSearch(e.target.value); setCrmPage(0); }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                        sx: { fontFamily, borderRadius: '10px' },
                    }}
                    sx={{ mb: 2 }}
                />

                <TableContainer sx={{ maxHeight: 380 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Name', 'Email', 'Phone', 'Company', ''].map(h => (
                                    <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', bgcolor: '#f8fafc' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {crmLoading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={5}><Skeleton height={32} /></TableCell></TableRow>
                                ))
                            ) : crmContacts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                            {crmSearch ? 'No contacts found matching your search' : 'No CRM contacts available'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : crmContacts.map(contact => (
                                <TableRow
                                    key={contact.id}
                                    hover
                                    onClick={() => !contact.already_linked && handleSelectContact(contact)}
                                    sx={{
                                        cursor: contact.already_linked ? 'default' : 'pointer',
                                        opacity: contact.already_linked ? 0.5 : 1,
                                        '&:hover': { bgcolor: contact.already_linked ? 'inherit' : '#f0fdf4' },
                                    }}
                                >
                                    <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 500, color: '#1e293b' }}>
                                        {contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim()}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.email || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.phone || '—'}</TableCell>
                                    <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{contact.company_name || '—'}</TableCell>
                                    <TableCell>
                                        {contact.already_linked ? (
                                            <Chip label="Already Linked" size="small" sx={{ fontFamily, fontSize: '0.65rem', height: 20, bgcolor: '#f1f5f9', color: '#94a3b8' }} />
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => { e.stopPropagation(); handleSelectContact(contact); }}
                                                sx={{ fontFamily, fontSize: '0.7rem', textTransform: 'none', borderRadius: '8px', borderColor: ACCENT, color: ACCENT, minWidth: 'auto', px: 1.5, '&:hover': { borderColor: '#16a34a', bgcolor: '#f0fdf4' } }}
                                            >
                                                Select
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                {crmTotal > 10 && (
                    <TablePagination
                        component="div"
                        count={crmTotal}
                        page={crmPage}
                        onPageChange={(_, p) => setCrmPage(p)}
                        rowsPerPage={10}
                        rowsPerPageOptions={[10]}
                        sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily, fontSize: '0.8rem' } }}
                    />
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Button
                    onClick={handleAddCrmContact}
                    startIcon={<PersonAddIcon />}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    disabled={!activeChatbot}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 600,
                        borderRadius: '10px', color: '#1d4ed8', bgcolor: '#dbeafe',
                        px: 1.75, py: 0.75,
                        '&:hover': { bgcolor: '#bfdbfe' },
                    }}
                >
                    Add to CRM
                </Button>
                <Button onClick={handleSkipCrm} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                    Create Without CRM Link
                </Button>
            </DialogActions>
        </>
    );

    // ---- Customer Form Step ----
    const renderForm = () => (
        <>
            <DialogTitle sx={{ fontFamily, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!isEdit && (
                        <IconButton size="small" onClick={() => { setStep('select'); setSelectedContact(null); }} sx={{ color: '#94a3b8', mr: 0.5 }}>
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    )}
                    {isEdit ? 'Edit Customer' : 'New Customer'}
                </Box>
                <IconButton size="small" onClick={onClose} sx={{ color: '#94a3b8' }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '10px', fontFamily }}>{error}</Alert>}

                {/* CRM Link Banner */}
                {selectedContact && (
                    <Alert
                        severity="success"
                        icon={<LinkIcon sx={{ color: ACCENT }} />}
                        sx={{ mb: 2, borderRadius: '10px', fontFamily, fontSize: '0.8rem', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}
                    >
                        Linked to CRM Contact: <strong>{selectedContact.full_name || `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim()}</strong>
                        {selectedContact.email && ` (${selectedContact.email})`}
                    </Alert>
                )}

                {/* Basic Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <TextField
                        label="Company Name" size="small" fullWidth
                        value={formData.company_name}
                        onChange={e => updateField('company_name', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="First Name" size="small" fullWidth
                            value={formData.first_name}
                            onChange={e => updateField('first_name', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Last Name" size="small" fullWidth
                            value={formData.last_name}
                            onChange={e => updateField('last_name', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Email" size="small" fullWidth type="email"
                            value={formData.email}
                            onChange={e => updateField('email', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Phone" size="small" fullWidth
                            value={formData.phone}
                            onChange={e => updateField('phone', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Payment & Credit */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Payment & Credit
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={labelSx}>Payment Terms</InputLabel>
                            <Select
                                value={formData.payment_terms}
                                onChange={e => updateField('payment_terms', e.target.value)}
                                label="Payment Terms"
                                sx={{ fontFamily, borderRadius: '10px' }}
                            >
                                {PAYMENT_TERMS_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value} sx={{ fontFamily }}>{opt.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Currency" size="small" sx={{ minWidth: 100 }}
                            value={formData.currency}
                            onChange={e => updateField('currency', e.target.value)}
                            InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                            label="Credit Limit" size="small" fullWidth type="number"
                            value={formData.credit_limit}
                            onChange={e => updateField('credit_limit', e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>$</InputAdornment>,
                                sx: fieldSx,
                            }}
                            InputLabelProps={{ sx: labelSx }}
                        />
                        <TextField
                            label="Warning %" size="small" sx={{ minWidth: 120 }} type="number"
                            value={formData.credit_limit_warning_pct}
                            onChange={e => updateField('credit_limit_warning_pct', e.target.value)}
                            InputProps={{
                                endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                sx: fieldSx,
                                inputProps: { min: 0, max: 100 },
                            }}
                            InputLabelProps={{ sx: labelSx }}
                            helperText="Alert when utilization exceeds this %"
                            FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                        />
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Tax Info */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Tax Information
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3, alignItems: 'center' }}>
                    <TextField
                        label="Tax ID" size="small" fullWidth
                        value={formData.tax_id}
                        onChange={e => updateField('tax_id', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formData.tax_exempt}
                                onChange={e => updateField('tax_exempt', e.target.checked)}
                                sx={{ '&.Mui-checked': { color: ACCENT } }}
                            />
                        }
                        label={<Typography sx={{ fontFamily, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Tax Exempt</Typography>}
                    />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Billing Address */}
                <Box sx={{ mb: 3 }}>
                    {renderAddressFields('billing_address', 'Billing Address')}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Shipping Address */}
                <Box sx={{ mb: 3 }}>
                    {renderAddressFields('shipping_address', 'Shipping Address')}
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Notes & Tags */}
                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b', mb: 1.5 }}>
                    Additional
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                        label="Notes" size="small" fullWidth multiline rows={3}
                        value={formData.notes}
                        onChange={e => updateField('notes', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                    />
                    <TextField
                        label="Tags" size="small" fullWidth
                        value={formData.tags}
                        onChange={e => updateField('tags', e.target.value)}
                        InputProps={{ sx: fieldSx }} InputLabelProps={{ sx: labelSx }}
                        helperText="Comma-separated tags (e.g. VIP, Wholesale, Priority)"
                        FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving || (!formData.company_name && !formData.first_name && !formData.last_name)}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                >
                    {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Customer'}
                </Button>
            </DialogActions>
        </>
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
            {!isEdit && step === 'select' ? renderCrmSelection() : renderForm()}
        </Dialog>
    );
}
